
import { gtcHandleize, gtcGetCookie, gtcSetCookie } from "gtcUtils";
import { gtcOpener } from "gtcOpener";

class GTCGeolocator extends gtcOpener {

  #shouldOpen = false;

  constructor() {
    super();

    this.FormSubmit = this.FormSubmit.bind(this);
    this.changeCountry = this.changeCountry.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.dom = {
      flagImg: this.querySelector("[data-geolocator-flag]"),
      countryList: this.querySelector("[data-country-list]"),
      form: this.querySelector("form")
    };

    this.settings = {
      ...this.settings,
      cookieName: "GTC_GEOLOCATOR",
      currentCountry: this.getAttribute("current-country") || "",
      currentPageType: this.getAttribute("current-page-type") || "",
      translate: this.getAttribute("translate"),
    };

    const url = new URL(window.location)
    if (url.searchParams.has('redirected')) {
      this.#shouldOpen = false;
      url.searchParams.delete('redirected');

      window.history.replaceState({}, document.title, url);
      gtcSetCookie(this.settings.cookieName, "true", 30);
    }

    if (gtcGetCookie(this.settings.cookieName) === null) {
      this.initGeoLocator();
    }

    this.addListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeListeners();
  }


  addListeners() {
    super.addListeners();
    this.dom.countryList.addEventListener("change", this.changeCountry);
    this.dom.form.addEventListener("submit", this.FormSubmit);

  }

  removeListeners() {
    super.removeListeners();
    this.dom.countryList.removeEventListener("change", this.changeCountry);
    this.dom.form.removeEventListener("submit", this.FormSubmit);
  }

  init() {
    super.init();
    this.initGeoLocator();
  }

  /**
 * Initializes the geolocator popup.
 * Fetches the user's country and updates the flag image accordingly.
 * Handles the country selection change event.
 * Updates the geolocator state based on the selected country.
 * If the selected country is the main country, it removes the active class and restores the body overflow.
 */
  async initGeoLocator() {   
    const countryList = this.dom.countryList
    const countryOptions = Array.from(countryList.options).map(option => option.value);
    const country = await this.getCountry();

    if (this.settings.translate) {
      this.translate(country);
    }

    if (countryList) {

      // If the user's country is not the same as the current country, open the geolocator.
      if (this.settings.currentCountry !== country) {

        // If the user's country is in the country options, select it and open the geolocator.
        if (countryOptions.includes(country)) {
          const indexOfCountry = countryOptions.indexOf(country);
          this.updateFlag(country);
          countryList.selectedIndex = indexOfCountry;
          this.#shouldOpen = true;
        } else {
          const internationalIndex = Array.from(countryList.options).findIndex(option => option.getAttribute("data-international") !== null);
          if (internationalIndex !== -1) {
            this.updateFlag(countryList.options[internationalIndex].value);
            countryList.selectedIndex = internationalIndex;
            this.#shouldOpen = true;
          }
        }
      }

      if (this.#shouldOpen) {
        this.open();
      }
    }
  }

  /**
   * Fetches the country based on the user's IP address using the GeoJS API.
   * Returns the country name in a handleized format.
   * @returns {Promise<string>} The handleized country name.
   */
  async getCountry() {
    const response = await fetch("https://get.geojs.io/v1/ip/country.json");
    const body = await response.json();
    return gtcHandleize(body.country);
  }

  /**
   * Updates the flag image based on the selected country.
   * @param {string} country - The selected country.
   */
  updateFlag(country) {
    const flagImgSrc = this.dom.flagImg.getAttribute("src");
    const flagImgSrcSplit = flagImgSrc.split("/");
    flagImgSrcSplit[flagImgSrcSplit.length - 1] = `${ country }.svg`;

    this.dom.flagImg.setAttribute("src", flagImgSrcSplit.join("/"));
  }

  async FormSubmit(e) {
    e.preventDefault();

    const countryList = this.dom.countryList;
    const selectedOption = countryList.options[countryList.selectedIndex];

    if (selectedOption.value == this.settings.currentCountry) {
      this.close();
    } else {
      if (selectedOption.getAttribute("data-url") !== null) {
        const newLocation = new URL(selectedOption.getAttribute("data-url"));
        
        const validRedirects = ["product", "collection", "page"];
        
        if (validRedirects.includes(this.settings.currentPageType)) {
          newLocation.pathname = window.location.pathname.slice(1);
          
          let response;
          try {
            response = await fetch(`${ newLocation.toString().replace('www.', '') }.json`);
          } catch (error) {
            console.warn('Fetch failed:', error);
          }
          
          if (response?.status == 200) {  
            newLocation.searchParams.append('redirected', 'true');
            window.location.href = newLocation.toString();
            return;
          }
        }

        window.location.href = newLocation.origin.toString() + "?redirected=true";
      } else {
        alert("No URL specified for this country.");
        this.close();
      }
    }
  }

  translate(country) {
    let lang = navigator.language || navigator.userLanguage;

    // if geojs lang
    if (this.settings.translate === "geojs") {
      lang = country;
    }

    const regionNamesByBrowser = new Intl.DisplayNames([lang, 'en'], { type: 'region' });

    for (let i = 0; i < this.dom.countryList.options.length; i++) {
      this.dom.countryList.options[i].text = regionNamesByBrowser.of(this.dom.countryList.options[i].value.toUpperCase())
    }
  }

  // Close the geolocator and set the cookie
  close() {
    super.close();
    gtcSetCookie(this.settings.cookieName, "true", 30);
  }

  changeCountry({ countryList = this.dom.countryList }) {
    const selectedCountry =
      countryList.options[countryList.selectedIndex].value;
    this.updateFlag(selectedCountry);
  }

}

if (!customElements.get("gtc-geolocator")) {
  customElements.define("gtc-geolocator", GTCGeolocator);
}